/* eslint-disable @typescript-eslint/no-explicit-any */
import { SUI_CHAIN_ID } from '../../../utils/constants';
import { AbstractWalletConnector } from '../../core/AbstractWalletConnector';
import { ChainTypes } from '../../core/types';
import { isBrowser } from '../../misc';
import { SuiWalletAccount, SuiWalletFeatureDef, SuiWalletFeatures } from './suiWalletTypes';

export class SuiConnector extends AbstractWalletConnector {
  readonly chainType = ChainTypes.SUI as const;

  private _provider?: any;
  public get provider() {
    return this._provider;
  }

  private _getProvider: () => Promise<any>;

  constructor(name: string, _provider: unknown) {
    super(name);
    this.chainId = SUI_CHAIN_ID;
    this._getProvider = async () => {
      const provider = typeof _provider === 'function' ? await _provider() : _provider;
      if (!provider) {
        return;
      }
      if (typeof provider === 'string') {
        return isBrowser ? window[provider] : undefined;
      }
      if (typeof provider === 'object') {
        return provider;
      }
      return;
    };
  }

  private _unSubscribeProvider: (() => void) | undefined;
  private _subscribeProvider(provider: any) {
    this._unSubscribeProvider?.();
    const eventBus = provider.features[SuiWalletFeatures.Events];
    if (!eventBus.on) {
      return;
    }
    const removeAccountChange = eventBus.on('accountChanged', (publicKey: any) => {
      console.log(publicKey);
    });

    const unSubscribe = () => {
      removeAccountChange();
      this._unSubscribeProvider = undefined;
    };
    this._unSubscribeProvider = unSubscribe;
  }

  public accounts: SuiWalletAccount[] = [];

  // sui 暂时不支持 悄悄恢复钱包
  async connect(_recovery = false) {
    const accounts: SuiWalletAccount[] = await this.invokeFeature({ feature: SuiWalletFeatures.Connect });
    this.accounts = accounts;
    const firstAccount = accounts[0];
    this._innerUseSetAddress(firstAccount.address, firstAccount.publicKey);
    return this.address;
  }

  async disconnect() {
    this._innerUseSetAddress(undefined, undefined);

    if (await this.hasFeature({ feature: SuiWalletFeatures.Disconnect })) {
      await this.invokeFeature({ feature: SuiWalletFeatures.Disconnect });
    }
    return;
  }

  async getProvider(force = false): Promise<any> {
    if (!this._provider || force) {
      const eipProvider = await this._getProvider();
      if (eipProvider) {
        this._provider = eipProvider;
        this._subscribeProvider(eipProvider);
      } else {
        this._provider = undefined;
      }
    }
    return this._provider;
  }
  /**
   * 检测钱包是否有某个feature
   * @param param0
   * @returns
   */
  async hasFeature({ feature, method, version }: SuiWalletFeatureDef) {
    const provider = await this.getProvider();
    if (!provider) {
      return false;
    }
    const featureObj = provider.features?.[feature];
    if (!featureObj) {
      return false;
    }
    if (version && featureObj.version !== version) {
      return false;
    }
    if (!method) {
      method = feature.split(':').pop();
    }
    if (!method) {
      return false;
    }
    return Boolean(feature[method]);
  }
  /**
   * 执行钱包feature 方法
   * @param param0
   * @param args
   * @returns
   */
  async invokeFeature({ feature, method, version }: SuiWalletFeatureDef, ...args: any[]) {
    const provider = await this.getProvider();
    if (!provider) {
      throw new Error(`${this.name} provider not found or not ready`);
    }
    const featureObj = provider.features?.[feature];
    if (!feature) {
      throw new Error(`not found sui feature: ${feature} on provider ${this.name}`);
    }
    if (!method) {
      method = feature.split(':').pop();
    }
    if (!method) {
      throw new Error(`${this.name} not specified feature method name.`);
    }
    if (version) {
      if (featureObj.version !== version) {
        throw new Error(`${this.name} wallet feature version: ${featureObj.version} not match requirements: ${version}.feature: ${feature}`);
      }
    }
    return featureObj[method](...args);
  }

  signMessage(...args: any[]) {
    return this.invokeFeature({ feature: SuiWalletFeatures.SignMessage }, ...args);
  }
  signPersonalMessage(...args: any[]) {
    return this.invokeFeature({ feature: SuiWalletFeatures.SignPersonalMessage }, ...args);
  }
  signAndExecuteTransaction(...args: any[]) {
    return this.invokeFeature({ feature: SuiWalletFeatures.SignAndExecuteTransaction }, ...args);
  }
  signAndExecuteTransactionBlock(...args: any[]) {
    return this.invokeFeature({ feature: SuiWalletFeatures.SignAndExecuteTransactionBlock }, ...args);
  }

  signTransaction(...args: any[]) {
    return this.invokeFeature({ feature: SuiWalletFeatures.SignTransaction }, ...args);
  }

  signTransactionBlock(...args: any[]) {
    return this.invokeFeature({ feature: SuiWalletFeatures.SignTransactionBlock }, ...args);
  }

  async isAvailable() {
    const provider = await this.getProvider();
    return Boolean(provider);
  }
}
